import React, { useState, useEffect } from "react";
import { useBusinessPlan } from "../App";
import { useSubscription } from "../contexts/SubscriptionContext";
import { useCurrency } from "../contexts/CurrencyContext";
import FeatureGate from "./FeatureGate";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Slider } from "./ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import TooltipLabel from "./ui/TooltipLabel";
import ExportButton from "./ui/ExportButton";
import { outputTooltips } from "../utils/outputSectionsTooltips";
import { useUserEntitlements } from "../hooks/useUserEntitlements";
import { LimitWarning, LimitIndicator } from "./governance";
import UpgradePrompt from "./governance/UpgradePrompt";
import {
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Target, 
  Activity, 
  ArrowUpRight, 
  ArrowDownRight,
  RefreshCw,
  PlayCircle,
  BarChart3,
  Zap,
  Users,
  Building2,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Calculator,
  Eye,
  Settings,
  PieChart,
  LineChart,
  Plus,
  Database,
  HelpCircle
} from "lucide-react";
import { 
  LineChart as RechartsLineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend,
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  PieChart as RechartsPieChart, 
  Pie, 
  Cell,
  AreaChart,
  Area,
  ComposedChart,
  ReferenceLine
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ScenarioModeling = () => {
  console.log('🎬 ScenarioModeling component rendering...');
  
  const { currentPlan } = useBusinessPlan();
  const { formatCurrency: formatCurrencyContext } = useCurrency();
  console.log('📋 Current Plan:', currentPlan);
  console.log('🆔 Plan ID:', currentPlan?.id);
  
  const { canCreate, getLimit } = useUserEntitlements();
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [activeScenario, setActiveScenario] = useState('realistic');
  const [scenarios, setScenarios] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showCreateScenario, setShowCreateScenario] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeData, setUpgradeData] = useState(null);
  const [scenarioForm, setScenarioForm] = useState({
    name: '',
    volume_multiplier: 1.0,
    price_multiplier: 1.0,
    opex_multiplier: 1.0,
    working_capital_multiplier: 1.0
  });
  
  // Legacy: Keep this for backwards compatibility, but will be populated from API
  const [scenarioParams, setScenarioParams] = useState({
    realistic: {
      revenueGrowth: 0,
      costReduction: 0,
      pricingChange: 0,
      marketExpansion: 0,
      operationalEfficiency: 0
    },
    optimistic: {
      revenueGrowth: 25,
      costReduction: 15,
      pricingChange: 10,
      marketExpansion: 30,
      operationalEfficiency: 20
    },
    pessimistic: {
      revenueGrowth: -15,
      costReduction: -5,
      pricingChange: -8,
      marketExpansion: -20,
      operationalEfficiency: -10
    }
  });

  // Base data and scenario results
  const [baseData, setBaseData] = useState({});
  const [scenarioResults, setScenarioResults] = useState({});
  const [comparisonData, setComparisonData] = useState([]);
  const [loadingBaseData, setLoadingBaseData] = useState(false);

  // Removed constraint validation features - keeping empty arrays for UI compatibility
  const constraintWarnings = [];
  const constraintErrors = [];
  const [showConstraintsDialog, setShowConstraintsDialog] = useState(false);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];
  
  console.log('🌐 BACKEND_URL:', BACKEND_URL);
  console.log('📊 Initial baseData state:', baseData);

  // Fetch base data from integrated sources
  const fetchBaseData = async () => {
    console.log('🔄 fetchBaseData called, currentPlan?.id:', currentPlan?.id);
    
    if (!currentPlan?.id) {
      console.warn('⚠️ fetchBaseData: No currentPlan.id, exiting early');
      return;
    }
    
    setLoadingBaseData(true);
    try {
      const apiUrl = `${BACKEND_URL}/api/scenario-base-data/${currentPlan.id}`;
      console.log('🌐 Fetching from URL:', apiUrl);
      
      const response = await axios.get(apiUrl);
      
      console.log('📦 Response received, status:', response.status);
      console.log('📊 RAW API RESPONSE:', JSON.stringify(response.data, null, 2));
      console.log('💰 Revenue from API:', response.data.revenue);
      console.log('💸 Costs from API:', response.data.costs);
      console.log('📈 Net Income from API:', response.data.netIncome);
      console.log('🎯 Strategic Initiatives count:', response.data.strategicInitiatives?.length || 0);
      console.log('📋 Suggested Parameters:', response.data.suggestedParameters);
      
      // Check if API returned zero values
      const hasZeroData = response.data.revenue === 0 && response.data.costs === 0 && response.data.netIncome === 0;
      if (hasZeroData) {
        console.warn('⚠️⚠️⚠️ API RETURNED ZERO VALUES FOR ALL FINANCIAL DATA ⚠️⚠️⚠️');
        console.warn('This likely means no Financial Planning data exists for this plan');
        console.warn('User needs to enter Revenue and Cost data in Financial Planning sections first');
      }
      
      // CRITICAL: Only update if we have valid data
      if (response.data && Object.keys(response.data).length > 0) {
        setBaseData(response.data);
        console.log('✅ Base data SET successfully');
        console.log('✅ baseData now contains:', {
          revenue: response.data.revenue,
          costs: response.data.costs,
          netIncome: response.data.netIncome,
          initiativesCount: response.data.strategicInitiatives?.length || 0
        });
      } else {
        console.warn('⚠️ API returned empty data, keeping existing baseData');
      }
    } catch (error) {
      console.error('❌ Error fetching scenario base data:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      // DON'T clear baseData on error - keep existing data
      console.warn('⚠️ Keeping existing baseData due to fetch error');
    } finally {
      console.log('🏁 fetchBaseData complete, setLoadingBaseData(false)');
      setLoadingBaseData(false);
    }
  };

  // Fetch scenarios and base data ONCE on mount
  useEffect(() => {
    console.log('⚡ useEffect triggered with currentPlan?.id:', currentPlan?.id);
    
    const initializeData = async () => {
      console.log('🔍 initializeData function started');
      console.log('🔍 currentPlan:', currentPlan);
      console.log('🔍 currentPlan?.id:', currentPlan?.id);
      
      if (!currentPlan?.id) {
        console.log('⚠️ No currentPlan.id, skipping initialization');
        console.log('⚠️ currentPlan object:', JSON.stringify(currentPlan));
        setLoading(false);
        return;
      }

      try {
        console.log('🚀 INITIALIZING ScenarioModeling for plan:', currentPlan.id);
        console.log('🚀 BACKEND_URL:', BACKEND_URL);
        setLoading(true);
        
        // Fetch base data first (includes strategic initiatives)
        console.log('📥 About to call fetchBaseData...');
        await fetchBaseData();
        console.log('✅ fetchBaseData completed');
        
        // Then fetch scenarios
        console.log('📥 About to call fetchScenarios...');
        await fetchScenarios();
        console.log('✅ fetchScenarios completed');
        
        console.log('✅ INITIALIZATION COMPLETE');
        console.log('📊 Final baseData state:', baseData);
      } catch (error) {
        console.error('❌ Error initializing data:', error);
        console.error('❌ Error stack:', error.stack);
      } finally {
        console.log('🏁 Setting loading to false');
        setLoading(false);
      }
    };

    console.log('🎯 Calling initializeData...');
    initializeData();
  }, [currentPlan?.id]);

  const fetchScenarios = async () => {
    try {
      console.log('📊 Fetching scenarios for plan:', currentPlan.id);
      const response = await axios.get(`${BACKEND_URL}/api/scenarios/${currentPlan.id}`);
      const fetchedScenarios = response.data;
      
      console.log('✅ Scenarios loaded:', fetchedScenarios.length);
      setScenarios(fetchedScenarios);
      
      // Convert scenarios array to params object for backwards compatibility
      const params = {};
      fetchedScenarios.forEach(scenario => {
        params[scenario.name] = scenario.parameters || {
          revenueGrowth: 0,
          costReduction: 0,
          pricingChange: 0,
          marketExpansion: 0,
          operationalEfficiency: 0
        };
      });
      
      setScenarioParams(params);
      
      // Set active scenario to first one if exists
      if (fetchedScenarios.length > 0 && !fetchedScenarios.find(s => s.name === activeScenario)) {
        setActiveScenario(fetchedScenarios[0].name);
      }
    } catch (error) {
      console.error('❌ Error fetching scenarios:', error);
      toast.error('Failed to load scenarios');
    }
  };

  // Save scenario changes to backend
  const saveScenarioChanges = async (scenarioName, updatedParams) => {
    try {
      setSaving(true);
      console.log('💾 Saving scenario:', scenarioName, updatedParams);
      console.log('📊 Current baseData BEFORE save:', { revenue: baseData.revenue, costs: baseData.costs, netIncome: baseData.netIncome });
      
      // Find the scenario ID
      const scenario = scenarios.find(s => s.name === scenarioName);
      
      if (!scenario) {
        console.error('❌ Scenario not found:', scenarioName);
        toast.error('Scenario not found');
        return;
      }

      // Update the scenario via API
      await axios.put(`${BACKEND_URL}/api/scenarios/${scenario.id}`, {
        name: scenarioName,
        plan_id: currentPlan.id,
        parameters: updatedParams,
        description: scenario.description
      });

      console.log('✅ Scenario saved successfully');
      console.log('📊 Current baseData AFTER save:', { revenue: baseData.revenue, costs: baseData.costs, netIncome: baseData.netIncome });
      
      toast.success('Scenario updated successfully!');
      
      // Refresh scenarios from backend (but DON'T refetch baseData)
      await fetchScenarios();
      
      console.log('📊 Current baseData AFTER fetchScenarios:', { revenue: baseData.revenue, costs: baseData.costs, netIncome: baseData.netIncome });
    } catch (error) {
      console.error('❌ Error saving scenario:', error);
      toast.error('Failed to save scenario: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Create new scenario
  const createNewScenario = async () => {
    try {
      if (!scenarioForm.name.trim()) {
        toast.error('Please enter a scenario name');
        return;
      }

      // CHECK LIMIT FIRST
      if (!canCreate('scenarios')) {
        const limit = getLimit('scenarios');
        
        // Show upgrade dialog immediately
        setUpgradeData({
          limitType: 'scenarios',
          currentUsage: limit?.current || 0,
          totalLimit: limit?.total || 0,
          upgradeOptions: [
            {
              type: 'tier_upgrade',
              tier: 'professional',
              tier_display: 'Professional Plan',
              price_monthly: 49,
              tier_limit: 3
            },
            {
              type: 'tier_upgrade',
              tier: 'business',
              tier_display: 'Business Plan',
              price_monthly: 149,
              tier_limit: 10
            }
          ],
          message: `You have reached your scenario limit (${limit?.total || 0})`
        });
        
        setShowUpgradeDialog(true);
        setShowCreateScenario(false);
        return; // Stop here
      }

      setSaving(true);
      console.log('➕ Creating new scenario:', scenarioForm.name);

      // Create scenario with form values
      await axios.post(`${BACKEND_URL}/api/scenarios`, {
        plan_id: currentPlan.id,
        name: scenarioForm.name.trim().toLowerCase(),
        volume_multiplier: parseFloat(scenarioForm.volume_multiplier),
        price_multiplier: parseFloat(scenarioForm.price_multiplier),
        opex_multiplier: parseFloat(scenarioForm.opex_multiplier),
        working_capital_multiplier: parseFloat(scenarioForm.working_capital_multiplier),
        parameters: {
          revenueGrowth: 0,
          costReduction: 0,
          pricingChange: 0,
          marketExpansion: 0,
          operationalEfficiency: 0
        }
      });

      console.log('✅ Scenario created successfully');
      toast.success('Scenario created successfully!');

      // Reset form and close dialog
      setScenarioForm({
        name: '',
        volume_multiplier: 1.0,
        price_multiplier: 1.0,
        opex_multiplier: 1.0,
        working_capital_multiplier: 1.0
      });
      setShowCreateScenario(false);

      // Refresh scenarios
      await fetchScenarios();
    } catch (error) {
      console.error('❌ Error creating scenario:', error);
      toast.error('Failed to create scenario: ' + (error.response?.data?.detail?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Format currency
  const formatCurrency = (amount, compact = false) => {
    if (!amount) return '$0';
    
    if (compact && Math.abs(amount) >= 1000000) {
      return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, false);
    }
    
    return formatCurrencyContext(amount, currentPlan?.currency || 'USD', null, false);
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (!value && value !== 0) return '0%';
    return `${value.toFixed(1)}%`;
  };

  // Static parameter impact data (SF integration removed)
  const calculateParameterImpact = () => {
    return [
      { parameter: 'Revenue Growth', profitSensitivity: 85, revenueSensitivity: 95, marginSensitivity: 15 },
      { parameter: 'Cost Reduction', profitSensitivity: 75, revenueSensitivity: 5, marginSensitivity: 70 },
      { parameter: 'Pricing Change', profitSensitivity: 65, revenueSensitivity: 80, marginSensitivity: 60 },
      { parameter: 'Market Expansion', profitSensitivity: 55, revenueSensitivity: 70, marginSensitivity: 25 },
      { parameter: 'Operational Efficiency', profitSensitivity: 45, revenueSensitivity: 10, marginSensitivity: 50 }
    ];
  };

  // Calculate scenario impact
  const calculateScenarioImpact = (params, base) => {
    // FIXED: Use multiplicative logic for percentage changes (not additive)
    const revenueMultiplier = (1 + (params.revenueGrowth / 100)) * 
                             (1 + (params.marketExpansion / 100)) * 
                             (1 + (params.pricingChange / 100));
    
    const costMultiplier = (1 - (params.costReduction / 100)) * 
                          (1 - (params.operationalEfficiency / 100));
    
    const newRevenue = base.revenue * revenueMultiplier;
    const newCosts = base.costs * costMultiplier;
    const newNetIncome = newRevenue - newCosts;
    const newGrossMargin = newRevenue > 0 ? ((newRevenue - newCosts) / newRevenue) * 100 : 0;
    
    // FIXED: Use actual cost structure from Cost Management (not hardcoded 60/40 split)
    const costStructure = base.costStructure || { variableRatio: 0.6, fixedRatio: 0.4 };
    const variableCosts = base.costs * costStructure.variableRatio;
    const fixedCosts = base.costs * costStructure.fixedRatio;
    
    // Break-even calculations using real data
    const totalUnits = base.totalUnits || base.breakEvenUnits || 1;
    const unitPrice = base.averagePrice || (base.revenue / Math.max(totalUnits, 1));
    const newUnitPrice = unitPrice * (1 + (params.pricingChange / 100));
    
    // Variable cost per unit with operational efficiency improvement
    const variableCostPerUnit = (variableCosts / Math.max(totalUnits, 1)) * 
                                (1 - (params.operationalEfficiency / 100));
    
    // Fixed costs adjusted for cost reduction
    const newFixedCosts = fixedCosts * (1 - (params.costReduction / 100));
    
    // Break-even units = Fixed Costs / (Price per unit - Variable cost per unit)
    const contributionMargin = Math.max(newUnitPrice - variableCostPerUnit, 0.01);
    const newBreakEvenUnits = newFixedCosts / contributionMargin;
    
    return {
      revenue: newRevenue,
      costs: newCosts,
      netIncome: newNetIncome,
      grossMargin: Math.max(0, newGrossMargin),
      breakEvenUnits: Math.max(0, newBreakEvenUnits),
      breakEvenRevenue: newBreakEvenUnits * newUnitPrice,
      revenueChange: base.revenue !== 0 ? ((newRevenue - base.revenue) / base.revenue) * 100 : 0,
      profitChange: base.netIncome !== 0 ? ((newNetIncome - base.netIncome) / Math.abs(base.netIncome)) * 100 : 0,
      marginChange: newGrossMargin - base.grossMargin,
      costStructureUsed: {
        variableRatio: costStructure.variableRatio,
        fixedRatio: costStructure.fixedRatio,
        variableCosts: variableCosts,
        fixedCosts: fixedCosts
      }
    };
  };

  // Run scenario analysis
  const runScenarioAnalysis = async () => {
    if (!Object.keys(baseData).length) return;

    setCalculating(true);
    
    // Simulate calculation delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const results = {};
    const comparison = [];

    Object.entries(scenarioParams).forEach(([scenario, params]) => {
      const result = calculateScenarioImpact(params, baseData);
      results[scenario] = result;
      
      comparison.push({
        scenario: scenario.charAt(0).toUpperCase() + scenario.slice(1),
        revenue: result.revenue,
        costs: result.costs,
        profit: result.netIncome,
        margin: result.grossMargin,
        breakEven: result.breakEvenUnits
      });
    });

    // Add base scenario for comparison
    comparison.unshift({
      scenario: 'Current',
      revenue: baseData.revenue,
      costs: baseData.costs,
      profit: baseData.netIncome,
      margin: baseData.grossMargin,
      breakEven: baseData.breakEvenUnits
    });

    setScenarioResults(results);
    setComparisonData(comparison);
    setCalculating(false);
  };

  // Removed Phase 1.3 & 1.4 SF features

  // Update scenario parameter (now saves to backend)
  const updateScenarioParam = (scenario, param, value) => {
    // Update local state immediately for responsive UI
    setScenarioParams(prev => {
      const updated = {
        ...prev,
        [scenario]: {
          ...prev[scenario],
          [param]: value
        }
      };
      
      // Removed financial constraints validation
      
      // Debounce save to backend (save after 1 second of no changes)
      if (updateScenarioParam.timeout) {
        clearTimeout(updateScenarioParam.timeout);
      }
      
      updateScenarioParam.timeout = setTimeout(() => {
        saveScenarioChanges(scenario, updated[scenario]);
      }, 1000);
      
      return updated;
    });
  };

  useEffect(() => {
    if (Object.keys(baseData).length > 0) {
      runScenarioAnalysis();
    }
  }, [baseData, scenarioParams]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading scenario modeling...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!currentPlan) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <BarChart3 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Business Plan Selected</h3>
            <p className="text-slate-600 mb-6">
              Please select or create a business plan to access scenario modeling.
            </p>
            <Button onClick={() => window.location.href = '/dashboard'} className="bg-blue-600 hover:bg-blue-700">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentResult = scenarioResults[activeScenario] || {};

  return (
    <FeatureGate 
      feature="scenariomodeling"
      title="Scenario Modeling & What-If Analysis"
      description="Advanced financial modeling with multiple scenarios, sensitivity analysis, and Monte Carlo simulations"
    >
      <div className="p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Strategic Scenario Planning & What-If Analysis</h1>
            <p className="text-slate-600 mt-1">
              Interactive financial modeling and scenario planning for {currentPlan.company_name || currentPlan.name}
            </p>
            <div className="mt-2 text-sm text-slate-500">
              💡 Hover over scenario cards and metrics for detailed explanations
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <ExportButton
              targetId="scenario-modeling-content"
              componentName="Strategic_Scenario_Planning"
              formats={['png']}
              size="sm"
              variant="outline"
              showIcon={true}
              showText={true}
            />
            <Button onClick={runScenarioAnalysis} disabled={calculating} className="bg-purple-600 hover:bg-purple-700">
              <RefreshCw className={`h-4 w-4 mr-2 ${calculating ? 'animate-spin' : ''}`} />
              {calculating ? 'Calculating...' : 'Recalculate'}
            </Button>
          </div>
        </div>

      <div id="scenario-modeling-content">
      {/* Real Data Sources card removed */}

      {/* Scenario Selector & Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Active Scenario</CardTitle>
                <CardDescription>Select scenario to analyze</CardDescription>
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => {
                  // Always allow opening form
                  setShowCreateScenario(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Create New
              </Button>
              
              {/* Governance: Compact Limit Display */}
              <div className="ml-4">
                <LimitIndicator limitType="scenarios" compact />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Select value={activeScenario} onValueChange={setActiveScenario}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {scenarios.map((scenario) => (
                  <SelectItem key={scenario.id} value={scenario.name}>
                    {scenario.name.charAt(0).toUpperCase() + scenario.name.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Quick Impact Metrics */}
        <TooltipLabel
          label={
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700 font-medium">Revenue Impact</p>
                    <p className="text-xl font-bold text-green-900">
                      {formatCurrency(currentResult.revenue, true)}
                    </p>
                    <div className="flex items-center mt-1">
                      {(currentResult.revenueChange || 0) >= 0 ? (
                        <ArrowUpRight className="h-4 w-4 text-green-600 mr-1" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4 text-red-600 mr-1" />
                      )}
                      <span className={`text-sm font-medium ${(currentResult.revenueChange || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercentage(Math.abs(currentResult.revenueChange || 0))}
                      </span>
                    </div>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
          }
          tooltip={{
            title: "Revenue Impact",
            content: "Shows the projected revenue for the selected scenario compared to your baseline realistic scenario. Positive changes indicate revenue growth, while negative changes show potential revenue decline under different market conditions.",
            example: "If realistic scenario projects $500K revenue and optimistic shows $625K, the impact is +25% or +$125K additional revenue."
          }}
        />

        <TooltipLabel
          label={
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-700 font-medium">Profit Impact</p>
                    <p className="text-xl font-bold text-blue-900">
                      {formatCurrency(currentResult.netIncome, true)}
                    </p>
                    <div className="flex items-center mt-1">
                      {(currentResult.profitChange || 0) >= 0 ? (
                        <ArrowUpRight className="h-4 w-4 text-green-600 mr-1" />
                      ) : (
                        <ArrowDownRight className="h-4 w-4 text-red-600 mr-1" />
                      )}
                      <span className={`text-sm font-medium ${(currentResult.profitChange || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercentage(Math.abs(currentResult.profitChange || 0))}
                      </span>
                    </div>
                  </div>
                  <TrendingUp className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
          }
          tooltip={{
            title: "Profit Impact",
            content: "Net income projected for this scenario compared to baseline. Shows how changes in volume, pricing, and costs affect bottom-line profitability. This is your actual profit after all expenses.",
            example: "Optimistic scenario with +20% volume and +5% pricing might show +35% profit impact due to operating leverage."
          }}
        />

        <TooltipLabel
          label={
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-700 font-medium">Break-Even</p>
                    <p className="text-xl font-bold text-purple-900">
                      {Math.round(currentResult.breakEvenUnits || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-purple-600 mt-1">units</p>
                  </div>
                  <Target className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          }
          tooltip={{
            title: "Break-Even Units",
            content: "Number of units you need to sell under this scenario to cover all costs (zero profit). Lower is better as it means less sales pressure to be profitable. Changes with pricing and cost assumptions.",
            example: "If break-even is 5,000 units and you plan to sell 8,000, you have a 3,000-unit safety cushion (60% margin of safety)."
          }}
        />
      </div>

      {/* Governance: Limit Warning */}
      <div className="mb-4">
        <LimitWarning limitType="scenarios" />
      </div>

      {/* Main Analysis Interface */}
      <Tabs defaultValue="parameters" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="parameters">Parameter Tuning</TabsTrigger>
          <TabsTrigger value="comparison">Scenario Comparison</TabsTrigger>
          <TabsTrigger value="sensitivity">Sensitivity Analysis</TabsTrigger>
        </TabsList>

        {/* Parameter Tuning Tab */}
        <TabsContent value="parameters" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Parameter Controls */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Settings className="h-5 w-5 mr-2 text-blue-600" />
                    Scenario Parameters
                  </div>
                  {saving && (
                    <Badge variant="secondary" className="animate-pulse">
                      Saving...
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Adjust variables for {activeScenario} scenario • Changes auto-save
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {[
                  { 
                    key: 'revenueGrowth', 
                    label: 'Revenue Growth', 
                    icon: TrendingUp, 
                    min: -50, 
                    max: 100, 
                    suffix: '%',
                    tooltip: 'Revenue Growth represents the expected year-over-year percentage increase in total sales revenue. This parameter uses real revenue data from your Financial Planning. Formula: New Revenue = Base Revenue × (1 + Growth %). Example: $1M base × 25% growth = $1.25M. This multiplies with other factors (market expansion, pricing). Industry benchmarks: SaaS 20-40% YoY, Retail 5-15% YoY, Startups 50-100%+ in early years. Negative values model recession scenarios. Range: -50% (severe contraction) to +100% (hyper-growth).'
                  },
                  { 
                    key: 'costReduction', 
                    label: 'Cost Reduction', 
                    icon: DollarSign, 
                    min: -25, 
                    max: 50, 
                    suffix: '%',
                    tooltip: 'Cost Reduction represents percentage savings in total operating costs through efficiency improvements, automation, renegotiated contracts, or process optimization. Uses real cost data from your Cost Management (COGS + OPEX). Formula: New Costs = Base Costs × (1 - Reduction %). Example: $600K base costs × 15% reduction = $510K (saves $90K). Primarily affects fixed costs; variable costs scale with production. Realistic targets: 5-10% from process improvements, 15-25% from automation, 30%+ from major restructuring. Negative values model cost inflation or expansion investments. Be conservative: aggressive cuts may hurt quality or growth.'
                  },
                  { 
                    key: 'pricingChange', 
                    label: 'Pricing Adjustment', 
                    icon: Target, 
                    min: -30, 
                    max: 50, 
                    suffix: '%',
                    tooltip: 'Pricing Adjustment models changes to your average product/service pricing. Multiplies with revenue growth and market expansion for total revenue impact. Formula: New Price = Base Price × (1 + Pricing Change %). Example: $100 product × 10% increase = $110. Uses average price from Sales Forecasts. Key considerations: (1) Price elasticity - higher prices may reduce volume, (2) Competitive positioning - too high vs. competitors loses customers, (3) Value perception - premium pricing requires premium value. Industry norms: Inflation adjustments 2-5% annually, Value-based increases 10-20%, Promotional discounts -10 to -30%. Test price sensitivity before implementing large changes.'
                  },
                  { 
                    key: 'marketExpansion', 
                    label: 'Market Expansion', 
                    icon: Building2, 
                    min: -40, 
                    max: 80, 
                    suffix: '%',
                    tooltip: 'Market Expansion represents revenue growth from entering new geographic markets, customer segments, or distribution channels. Multiplies with revenue growth for compound effect. Formula: New Market Size = Current Market × (1 + Expansion %). Example: Expanding from US to EU might add 30% market size. Linked to Strategic Framework initiatives (e.g., "Market Expansion - EU"). Types: (1) Geographic - new regions/countries, (2) Demographic - new customer segments, (3) Channel - online→retail or B2C→B2B. Costs: Market research, localization, marketing, partnerships. Realistic timeline: 6-18 months to see results. Risk: Cultural fit, regulatory compliance, competition. Range: -40% (market contraction/exit) to +80% (major expansion).'
                  },
                  { 
                    key: 'operationalEfficiency', 
                    label: 'Operational Efficiency', 
                    icon: Activity, 
                    min: -20, 
                    max: 40, 
                    suffix: '%',
                    tooltip: 'Operational Efficiency measures productivity improvements that reduce variable costs per unit. Affects break-even calculations using your real cost structure from Cost Management. Formula: New Variable Cost/Unit = Base Variable Cost × (1 - Efficiency %). Example: $20/unit variable cost × 15% efficiency = $17/unit (save $3 per unit). Drivers: (1) Process automation - eliminate manual steps, (2) Technology - faster/better tools, (3) Training - skilled workforce, (4) Lean practices - reduce waste, (5) Economies of scale - higher volume lowers per-unit costs. Realistic gains: 5-10% from training, 15-25% from automation, 30%+ from major process redesign. Primarily impacts variable costs (materials, labor); fixed costs addressed via Cost Reduction parameter. Track via operational KPIs: units per hour, defect rate, cycle time.'
                  }
                ].map((param) => {
                  const Icon = param.icon;
                  const currentValue = scenarioParams[activeScenario]?.[param.key] || 0;
                  
                  return (
                    <div key={param.key} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Label className="flex items-center text-sm font-medium cursor-help">
                                <Icon className="h-4 w-4 mr-2 text-slate-600" />
                                {param.label}
                                <HelpCircle className="h-3 w-3 ml-1 text-slate-400" />
                              </Label>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>{param.tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <span className="text-sm font-bold text-slate-900">
                          {currentValue}{param.suffix}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <Slider
                          value={[currentValue]}
                          onValueChange={([value]) => updateScenarioParam(activeScenario, param.key, value)}
                          min={param.min}
                          max={param.max}
                          step={1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>{param.min}{param.suffix}</span>
                          <span>{param.max}{param.suffix}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Real-time Impact Chart */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={<CardTitle>Impact Visualization</CardTitle>}
                  tooltip="Impact Visualization shows real-time financial impact of your scenario adjustments using integrated data from Financial Planning, Cost Management, and Strategic Framework. Chart displays: Revenue (blue bars), Costs (orange bars), and Net Income (green line) across scenarios. Uses scientifically correct formulas: (1) Multiplicative revenue growth (not additive), (2) Real variable/fixed cost split from your Cost Management (not assumed 60/40), (3) Actual break-even calculations. Adjust scenario parameters above and watch this chart update instantly. Compare scenarios to see: Which grows revenue fastest? Which is most profitable? Which reaches break-even soonest? Use to test assumptions before committing resources."
                  asChild
                />
                <CardDescription>Real-time scenario impact analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={comparisonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="scenario" stroke="#64748b" />
                      <YAxis stroke="#64748b" />
                      <RechartsTooltip 
                        formatter={(value, name) => [
                          name.includes('revenue') || name.includes('costs') || name.includes('profit') ? 
                            formatCurrency(value, true) : 
                            name.includes('margin') ? formatPercentage(value) : 
                            Math.round(value).toLocaleString(),
                          name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1').trim()
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                      <Bar dataKey="profit" fill="#10b981" name="Profit" />
                      <Line 
                        type="monotone" 
                        dataKey="margin" 
                        stroke="#f59e0b" 
                        strokeWidth={3}
                        name="Margin %"
                      />
                      <ReferenceLine 
                        y={baseData.revenue} 
                        stroke="#ef4444" 
                        strokeDasharray="5 5" 
                        label="Current Revenue"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Scenario Comparison Tab */}
        <TabsContent value="comparison" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Comparison Table */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={<CardTitle>Scenario Comparison</CardTitle>}
                  tooltip="Scenario Comparison shows side-by-side analysis of key financial metrics across all scenarios (Realistic, Optimistic, Pessimistic). Use this to: (1) Understand the range of possible outcomes, (2) Identify which metrics are most sensitive to assumptions, (3) Prepare contingency plans for worst-case scenarios, (4) Communicate uncertainty to stakeholders. Example: Realistic $500K revenue, Optimistic $625K (+25%), Pessimistic $400K (-20%) shows ±25% revenue uncertainty. Look for: Large variance = high risk/opportunity, Small variance = predictable outcomes. Compare: Revenue (top-line growth), Net Income (bottom-line profitability), Break-Even (runway to sustainability), Gross Margin (pricing power). Use filters to model base case, aggressive growth, recession, and what-if scenarios."
                  asChild
                />
                <CardDescription>Side-by-side analysis of all scenarios</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-medium text-slate-700">Metric</th>
                        <th className="text-center py-2 font-medium text-slate-700">Current</th>
                        <th className="text-center py-2 font-medium text-green-700">Realistic</th>
                        <th className="text-center py-2 font-medium text-blue-700">Optimistic</th>
                        <th className="text-center py-2 font-medium text-orange-700">Pessimistic</th>
                      </tr>
                    </thead>
                    <tbody className="space-y-1">
                      {[
                        { 
                          label: 'Revenue', 
                          current: formatCurrency(baseData.revenue, true),
                          realistic: formatCurrency(scenarioResults.realistic?.revenue, true),
                          optimistic: formatCurrency(scenarioResults.optimistic?.revenue, true),
                          pessimistic: formatCurrency(scenarioResults.pessimistic?.revenue, true)
                        },
                        { 
                          label: 'Net Income', 
                          current: formatCurrency(baseData.netIncome, true),
                          realistic: formatCurrency(scenarioResults.realistic?.netIncome, true),
                          optimistic: formatCurrency(scenarioResults.optimistic?.netIncome, true),
                          pessimistic: formatCurrency(scenarioResults.pessimistic?.netIncome, true)
                        },
                        { 
                          label: 'Gross Margin', 
                          current: formatPercentage(baseData.grossMargin),
                          realistic: formatPercentage(scenarioResults.realistic?.grossMargin),
                          optimistic: formatPercentage(scenarioResults.optimistic?.grossMargin),
                          pessimistic: formatPercentage(scenarioResults.pessimistic?.grossMargin)
                        },
                        { 
                          label: 'Break-Even Units', 
                          current: Math.round(baseData.breakEvenUnits || 0).toLocaleString(),
                          realistic: Math.round(scenarioResults.realistic?.breakEvenUnits || 0).toLocaleString(),
                          optimistic: Math.round(scenarioResults.optimistic?.breakEvenUnits || 0).toLocaleString(),
                          pessimistic: Math.round(scenarioResults.pessimistic?.breakEvenUnits || 0).toLocaleString()
                        }
                      ].map((row, index) => (
                        <tr key={index} className="border-b border-slate-100">
                          <td className="py-2 font-medium text-slate-900">{row.label}</td>
                          <td className="py-2 text-center text-slate-700">{row.current}</td>
                          <td className="py-2 text-center text-green-700 font-medium">{row.realistic}</td>
                          <td className="py-2 text-center text-blue-700 font-medium">{row.optimistic}</td>
                          <td className="py-2 text-center text-orange-700 font-medium">{row.pessimistic}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Risk-Return Analysis */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={<CardTitle>Risk-Return Analysis</CardTitle>}
                  tooltip={{
                    title: "Risk-Return Analysis",
                    content: "Evaluates the probability and potential impact of each scenario. Shows the likelihood of each outcome (pessimistic, realistic, optimistic) and the magnitude of profit impact. Helps you understand risk vs. reward tradeoffs.",
                    example: "Realistic scenario: 60% probability, +5% profit impact. Optimistic: 20% probability, +25% impact. Choose strategy based on risk appetite."
                  }}
                  asChild
                />
                <CardDescription>Probability vs. impact assessment</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      {
                        scenario: 'Pessimistic',
                        probability: 20,
                        impact: scenarioResults.pessimistic?.profitChange || -15,
                        risk: 'High'
                      },
                      {
                        scenario: 'Realistic',
                        probability: 60,
                        impact: scenarioResults.realistic?.profitChange || 5,
                        risk: 'Medium'
                      },
                      {
                        scenario: 'Optimistic',
                        probability: 20,
                        impact: scenarioResults.optimistic?.profitChange || 25,
                        risk: 'Low'
                      }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="scenario" stroke="#64748b" />
                      <YAxis stroke="#64748b" />
                      <RechartsTooltip 
                        formatter={(value, name) => [
                          name === 'probability' ? `${value}%` : `${value.toFixed(1)}%`,
                          name === 'probability' ? 'Probability' : 'Profit Impact'
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="probability" fill="#3b82f6" name="Probability %" />
                      <Bar dataKey="impact" fill="#10b981" name="Profit Impact %" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sensitivity Analysis Tab */}
        <TabsContent value="sensitivity" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Parameter Impact Analysis */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={
                    <CardTitle className="flex items-center">
                      <Calculator className="h-5 w-5 mr-2 text-blue-600" />
                      Parameter Impact Analysis
                    </CardTitle>
                  }
                  tooltip={{
                    title: "Sensitivity Analysis",
                    content: "Measures how sensitive your financial outcomes are to changes in key assumptions. Shows which parameters have the biggest impact on profitability, helping you focus on what matters most and understand your risk exposure.",
                    example: "If a 10% change in pricing causes a 35% change in profit, but a 10% change in volume causes only 15% profit change, pricing is your most sensitive lever."
                  }}
                  asChild
                />
                <CardDescription>Sensitivity of key metrics to parameter changes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={calculateParameterImpact()}
                      layout="horizontal"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis type="number" domain={[0, 100]} stroke="#64748b" />
                      <YAxis dataKey="parameter" type="category" width={120} stroke="#64748b" fontSize={11} />
                      <RechartsTooltip 
                        formatter={(value, name) => [
                          `${value}%`,
                          name === 'profitSensitivity' ? 'Profit Impact' :
                          name === 'revenueSensitivity' ? 'Revenue Impact' : 'Margin Impact'
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="profitSensitivity" fill="#10b981" name="Profit Sensitivity" />
                      <Bar dataKey="revenueSensitivity" fill="#3b82f6" name="Revenue Sensitivity" />
                      <Bar dataKey="marginSensitivity" fill="#f59e0b" name="Margin Sensitivity" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Tornado Chart */}
            <Card>
              <CardHeader>
                <TooltipLabel
                  label={
                    <CardTitle className="flex items-center">
                      <Target className="h-5 w-5 mr-2 text-green-600" />
                      Tornado Analysis
                    </CardTitle>
                  }
                  tooltip={{
                    title: "Tornado Analysis",
                    content: "Shows the range of outcomes when each parameter varies by ±20%. Parameters are ordered by impact magnitude (largest at top). Identifies which assumptions drive the most variability in results, helping prioritize where to reduce uncertainty.",
                    example: "If Revenue Growth variation causes $32K swing but Operational Efficiency only $15K, focus on getting revenue assumptions right first."
                  }}
                  asChild
                />
                <CardDescription>Range of impact for ±20% parameter variation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { param: 'Revenue Growth', low: -24000, high: 32000, baseline: 0, color: '#10b981' },
                    { param: 'Cost Reduction', low: -18000, high: 28000, baseline: 0, color: '#3b82f6' },
                    { param: 'Market Expansion', low: -15000, high: 25000, baseline: 0, color: '#f59e0b' },
                    { param: 'Pricing Change', low: -12000, high: 22000, baseline: 0, color: '#8b5cf6' },
                    { param: 'Operational Efficiency', low: -8000, high: 15000, baseline: 0, color: '#06b6d4' }
                  ].map((item, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-slate-700">{item.param}</span>
                        <span className="text-xs text-slate-500">
                          {formatCurrency(item.low)} to {formatCurrency(item.high)}
                        </span>
                      </div>
                      
                      {/* Tornado bar visualization */}
                      <div className="relative h-6 bg-slate-100 rounded">
                        {/* Negative impact (left side) */}
                        <div 
                          className="absolute left-0 top-0 h-full rounded-l opacity-60"
                          style={{ 
                            width: `${Math.abs(item.low) / 40000 * 50}%`,
                            backgroundColor: '#ef4444'
                          }}
                        ></div>
                        
                        {/* Positive impact (right side) */}
                        <div 
                          className="absolute right-0 top-0 h-full rounded-r opacity-80"
                          style={{ 
                            width: `${item.high / 40000 * 50}%`,
                            backgroundColor: item.color
                          }}
                        ></div>
                        
                        {/* Center line */}
                        <div className="absolute left-1/2 top-0 w-0.5 h-full bg-slate-400 transform -translate-x-0.5"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Correlation Matrix */}
          <Card>
            <CardHeader>
              <TooltipLabel
                label={
                  <CardTitle className="flex items-center">
                    <Activity className="h-5 w-5 mr-2 text-purple-600" />
                    Parameter Correlation Matrix
                  </CardTitle>
                }
                tooltip={{
                  title: "Parameter Correlation Matrix",
                  content: "Shows how parameters move together or in opposite directions. Positive correlation (0 to +1) means they move together; negative (-1 to 0) means they move opposite. Helps identify dependencies and compound effects in your model.",
                  example: "Revenue Growth and Market Expansion have 0.85 correlation (strong positive) - when market expands, revenue grows proportionally. Useful for understanding cascading impacts."
                }}
                asChild
              />
              <CardDescription>How changes in one parameter affect others</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Correlation Heatmap */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-4">Cross-Parameter Correlations</h4>
                  <div className="space-y-2">
                    {[
                      { param1: 'Revenue Growth', param2: 'Market Expansion', correlation: 0.85, strength: 'Strong Positive' },
                      { param1: 'Cost Reduction', param2: 'Operational Efficiency', correlation: 0.72, strength: 'Moderate Positive' },
                      { param1: 'Pricing Change', param2: 'Revenue Growth', correlation: 0.68, strength: 'Moderate Positive' },
                      { param1: 'Market Expansion', param2: 'Cost Reduction', correlation: -0.23, strength: 'Weak Negative' },
                      { param1: 'Operational Efficiency', param2: 'Pricing Change', correlation: 0.31, strength: 'Weak Positive' }
                    ].map((corr, index) => (
                      <div key={index} className="flex items-center justify-between bg-slate-50 p-3 rounded">
                        <div className="flex-1">
                          <span className="text-sm font-medium text-slate-700">
                            {corr.param1} ↔ {corr.param2}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-4 h-4 rounded"
                            style={{ 
                              backgroundColor: corr.correlation > 0.5 ? '#10b981' : 
                                             corr.correlation > 0 ? '#f59e0b' : '#ef4444'
                            }}
                          ></div>
                          <span className="text-sm font-bold text-slate-900">
                            {corr.correlation.toFixed(2)}
                          </span>
                          <Badge 
                            variant={corr.correlation > 0.5 ? "success" : 
                                   Math.abs(corr.correlation) > 0.3 ? "warning" : "secondary"}
                            className="text-xs"
                          >
                            {corr.strength}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Monte Carlo Simulation Preview */}
                <div>
                  <TooltipLabel
                    label={<h4 className="font-medium text-slate-900 mb-4">Monte Carlo Simulation</h4>}
                    tooltip={{
                      title: "Monte Carlo Simulation",
                      content: "Runs thousands of scenarios with random variations in parameters to show the distribution of possible outcomes. The shaded area shows the confidence interval - where results are likely to fall. Helps quantify uncertainty and assess risk.",
                      example: "If 10,000 simulations show net income between $80K-$120K 90% of the time, you can plan with confidence that your likely range is $80K-$120K."
                    }}
                    asChild
                  />
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={(() => {
                        // Generate Monte Carlo simulation data
                        const simData = [];
                        for (let i = 0; i <= 100; i += 5) {
                          const percentile = i;
                          // Simulate profit outcomes at different percentiles
                          const profit = baseData.netIncome + (Math.random() * 40000 - 20000) * (1 + i/100);
                          simData.push({
                            percentile: percentile,
                            profit: Math.max(0, profit),
                            lowerBound: Math.max(0, profit * 0.8),
                            upperBound: profit * 1.3
                          });
                        }
                        return simData;
                      })()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="percentile" stroke="#64748b" />
                        <YAxis stroke="#64748b" />
                        <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                        <Area 
                          dataKey="upperBound" 
                          stackId="confidence" 
                          stroke="#3b82f6" 
                          fill="#3b82f6" 
                          fillOpacity={0.1}
                        />
                        <Area 
                          dataKey="profit" 
                          stackId="confidence" 
                          stroke="#10b981" 
                          fill="#10b981" 
                          fillOpacity={0.3}
                        />
                        <Area 
                          dataKey="lowerBound" 
                          stackId="confidence" 
                          stroke="#f59e0b" 
                          fill="#ffffff" 
                          fillOpacity={0.1}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-3 text-center">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">
                      10,000 Simulations Run
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sensitivity Summary */}
          <Card>
            <CardHeader>
              <TooltipLabel
                label={<CardTitle>Sensitivity Summary & Recommendations</CardTitle>}
                tooltip={{
                  title: "Sensitivity Summary & Recommendations",
                  content: "Actionable insights from all sensitivity analyses. Identifies high-impact opportunities (focus here for gains), high-risk exposures (mitigate these), and strategic recommendations based on your business's specific sensitivity profile.",
                  example: "High Impact: Focus on revenue growth (35% profit sensitivity). High Risk: Market dependency strong. Recommendation: Diversify revenue streams to reduce market concentration risk."
                }}
                asChild
              />
              <CardDescription>Key insights from sensitivity analysis</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <h5 className="font-medium text-green-600 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    High Impact Parameters
                  </h5>
                  <div className="space-y-2">
                    <div className="bg-green-50 p-2 rounded text-sm">
                      <span className="font-medium">Revenue Growth:</span> 85% profit sensitivity
                    </div>
                    <div className="bg-green-50 p-2 rounded text-sm">
                      <span className="font-medium">Cost Reduction:</span> 75% profit sensitivity
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h5 className="font-medium text-orange-600 flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Risk Factors
                  </h5>
                  <div className="space-y-2">
                    <div className="bg-orange-50 p-2 rounded text-sm">
                      <span className="font-medium">Market Dependency:</span> High correlation with expansion
                    </div>
                    <div className="bg-orange-50 p-2 rounded text-sm">
                      <span className="font-medium">Cost Volatility:</span> Moderate operational risk
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h5 className="font-medium text-blue-600 flex items-center">
                    <Target className="h-4 w-4 mr-2" />
                    Recommendations
                  </h5>
                  <div className="space-y-2">
                    <div className="bg-blue-50 p-2 rounded text-sm">
                      <span className="font-medium">Focus:</span> Revenue growth initiatives
                    </div>
                    <div className="bg-blue-50 p-2 rounded text-sm">
                      <span className="font-medium">Monitor:</span> Cost efficiency ratios
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Strategic Framework features removed */}
      {false && (
        <Card className="mt-6 border-l-4 border-l-purple-500">
          <CardHeader>
            <div className="flex items-center justify-between">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <CardTitle className="flex items-center text-purple-900 cursor-help">
                        <Zap className="h-5 w-5 mr-2" />
                        Strategic Initiatives ({baseData.strategicInitiatives.length})
                        <HelpCircle className="h-4 w-4 ml-2 text-purple-600" />
                      </CardTitle>
                      <CardDescription>
                        Map your strategic initiatives to scenario parameters
                      </CardDescription>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-md">
                    <div className="space-y-2">
                      <p className="font-semibold">Auto-Mapping from Strategic Framework</p>
                      <p className="text-sm">Your strategic initiatives are automatically mapped to scenario parameters based on keywords in their names or descriptions:</p>
                      <ul className="text-xs space-y-1 list-disc pl-4">
                        <li><strong>Revenue:</strong> "revenue growth", "sales growth", "increase sales" → +10% default</li>
                        <li><strong>Cost:</strong> "cost reduction", "cost saving", "reduce costs" → +10% default</li>
                        <li><strong>Pricing:</strong> "pricing", "price increase", "premium pricing" → +5% default</li>
                        <li><strong>Market:</strong> "market expansion", "new market", "international" → +15% default</li>
                        <li><strong>Operations:</strong> "operational efficiency", "automation", "productivity" → +12% default</li>
                      </ul>
                      <p className="text-xs text-slate-500 italic mt-2">Tip: Include these keywords in initiative names for auto-mapping</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowInitiativesDialog(true)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View All Details
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Open detailed view of all strategic initiatives with full parameter mappings and apply options</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {baseData.strategicInitiatives.map((initiative, idx) => {
                const hasMapping = initiative.parameter_mapping && 
                                  Object.values(initiative.parameter_mapping).some(v => v > 0);
                
                return (
                  <Card key={idx} className="border border-purple-200 hover:border-purple-400 transition-colors">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm text-slate-900">{initiative.name}</h4>
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{initiative.description}</p>
                          </div>
                          {hasMapping && (
                            <Badge variant="secondary" className="ml-2 bg-purple-100 text-purple-700">
                              Mapped
                            </Badge>
                          )}
                        </div>
                        
                        {initiative.budget > 0 && (
                          <div className="text-xs text-slate-500">
                            Budget: {formatCurrency(initiative.budget)}
                          </div>
                        )}
                        
                        {hasMapping && (
                          <div className="space-y-1 text-xs">
                            <p className="font-semibold text-slate-700">Parameter Impacts:</p>
                            {initiative.parameter_mapping.revenueGrowth > 0 && (
                              <div className="flex justify-between">
                                <span>Revenue Growth:</span>
                                <span className="font-medium text-green-600">+{initiative.parameter_mapping.revenueGrowth}%</span>
                              </div>
                            )}
                            {initiative.parameter_mapping.costReduction > 0 && (
                              <div className="flex justify-between">
                                <span>Cost Reduction:</span>
                                <span className="font-medium text-blue-600">+{initiative.parameter_mapping.costReduction}%</span>
                              </div>
                            )}
                            {initiative.parameter_mapping.pricingChange > 0 && (
                              <div className="flex justify-between">
                                <span>Pricing Change:</span>
                                <span className="font-medium text-purple-600">+{initiative.parameter_mapping.pricingChange}%</span>
                              </div>
                            )}
                            {initiative.parameter_mapping.marketExpansion > 0 && (
                              <div className="flex justify-between">
                                <span>Market Expansion:</span>
                                <span className="font-medium text-orange-600">+{initiative.parameter_mapping.marketExpansion}%</span>
                              </div>
                            )}
                            {initiative.parameter_mapping.operationalEfficiency > 0 && (
                              <div className="flex justify-between">
                                <span>Operational Efficiency:</span>
                                <span className="font-medium text-teal-600">+{initiative.parameter_mapping.operationalEfficiency}%</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Removed initiative parameter application feature */}
                        
                        {!hasMapping && (
                          <div className="text-xs text-slate-400 italic">
                            No parameter mappings defined
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {baseData.suggestedParameters && Object.values(baseData.suggestedParameters).some(v => v > 0) && (
              <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm text-purple-900 mb-2">Suggested Combined Parameters</h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                      {baseData.suggestedParameters.revenueGrowth > 0 && (
                        <div>
                          <span className="text-slate-600">Revenue Growth:</span>
                          <span className="font-bold text-green-700 ml-1">+{baseData.suggestedParameters.revenueGrowth}%</span>
                        </div>
                      )}
                      {baseData.suggestedParameters.costReduction > 0 && (
                        <div>
                          <span className="text-slate-600">Cost Reduction:</span>
                          <span className="font-bold text-blue-700 ml-1">+{baseData.suggestedParameters.costReduction}%</span>
                        </div>
                      )}
                      {baseData.suggestedParameters.pricingChange > 0 && (
                        <div>
                          <span className="text-slate-600">Pricing:</span>
                          <span className="font-bold text-purple-700 ml-1">+{baseData.suggestedParameters.pricingChange}%</span>
                        </div>
                      )}
                      {baseData.suggestedParameters.marketExpansion > 0 && (
                        <div>
                          <span className="text-slate-600">Market Expansion:</span>
                          <span className="font-bold text-orange-700 ml-1">+{baseData.suggestedParameters.marketExpansion}%</span>
                        </div>
                      )}
                      {baseData.suggestedParameters.operationalEfficiency > 0 && (
                        <div>
                          <span className="text-slate-600">Efficiency:</span>
                          <span className="font-bold text-teal-700 ml-1">+{baseData.suggestedParameters.operationalEfficiency}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 ml-4"
                          onClick={() => {
                            const currentParams = scenarioParams[activeScenario] || {};
                            const updatedParams = {
                              revenueGrowth: (currentParams.revenueGrowth || 0) + (baseData.suggestedParameters.revenueGrowth || 0),
                              costReduction: (currentParams.costReduction || 0) + (baseData.suggestedParameters.costReduction || 0),
                              pricingChange: (currentParams.pricingChange || 0) + (baseData.suggestedParameters.pricingChange || 0),
                              marketExpansion: (currentParams.marketExpansion || 0) + (baseData.suggestedParameters.marketExpansion || 0),
                              operationalEfficiency: (currentParams.operationalEfficiency || 0) + (baseData.suggestedParameters.operationalEfficiency || 0)
                            };
                            
                            setScenarioParams(prev => ({
                              ...prev,
                              [activeScenario]: updatedParams
                            }));
                            saveScenarioChanges(activeScenario, updatedParams);
                            toast.success('Applied all initiative suggestions!');
                          }}
                        >
                          <Zap className="h-4 w-4 mr-2" />
                          Apply All
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Apply combined impact from all strategic initiatives to {activeScenario} scenario</p>
                        <p className="text-xs text-slate-400 mt-1">Aggregates all initiative parameters and validates constraints</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Financial constraints features removed */}
      {false && (
        <Card className="mt-6 border-l-4 border-l-orange-500">
          <CardHeader>
            <CardTitle className="flex items-center text-orange-900">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Financial Constraints & Warnings
            </CardTitle>
            <CardDescription>
              Your scenario is being validated against financial health constraints
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {constraintErrors.map((error, idx) => (
                <div key={`error-${idx}`} className="flex items-start p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-900">Constraint Violation</p>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              ))}
              
              {constraintWarnings.map((warning, idx) => (
                <div key={`warning-${idx}`} className="flex items-start p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-yellow-900">Warning</p>
                    <p className="text-sm text-yellow-700 mt-1">{warning}</p>
                  </div>
                </div>
              ))}
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowConstraintsDialog(true)}
                className="mt-2"
              >
                <Eye className="h-4 w-4 mr-2" />
                View All Constraints
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      </div>

      {/* Create Scenario Dialog */}
      <Dialog open={showCreateScenario} onOpenChange={setShowCreateScenario}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New Scenario</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label htmlFor="scenario-name">Scenario Name *</Label>
              <Input
                id="scenario-name"
                value={scenarioForm.name}
                onChange={(e) => setScenarioForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Expansion Plan, Economic Downturn, New Market Entry"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="volume-multiplier">Volume Multiplier</Label>
                <Input
                  id="volume-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.volume_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, volume_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
                <p className="text-xs text-slate-500 mt-1">1.0 = no change, 1.2 = +20% volume</p>
              </div>

              <div>
                <Label htmlFor="price-multiplier">Price Multiplier</Label>
                <Input
                  id="price-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.price_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, price_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
                <p className="text-xs text-slate-500 mt-1">1.0 = no change, 1.1 = +10% price</p>
              </div>

              <div>
                <Label htmlFor="opex-multiplier">OpEx Multiplier</Label>
                <Input
                  id="opex-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.opex_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, opex_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
                <p className="text-xs text-slate-500 mt-1">1.0 = no change, 0.9 = -10% OpEx</p>
              </div>

              <div>
                <Label htmlFor="wc-multiplier">Working Capital Multiplier</Label>
                <Input
                  id="wc-multiplier"
                  type="number"
                  step="0.01"
                  min="0"
                  max="5"
                  value={scenarioForm.working_capital_multiplier}
                  onChange={(e) => setScenarioForm(prev => ({ ...prev, working_capital_multiplier: parseFloat(e.target.value) || 1.0 }))}
                />
                <p className="text-xs text-slate-500 mt-1">1.0 = no change, 1.15 = +15% WC</p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">Scenario Tips</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Volume affects sales quantities/units</li>
                    <li>• Price impacts revenue per unit/service</li>
                    <li>• OpEx affects operating expenses</li>
                    <li>• Working Capital affects cash flow timing</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateScenario(false);
                  setScenarioForm({
                    name: '',
                    volume_multiplier: 1.0,
                    price_multiplier: 1.0,
                    opex_multiplier: 1.0,
                    working_capital_multiplier: 1.0
                  });
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={createNewScenario}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {saving ? 'Creating...' : 'Create Scenario'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Strategic Initiatives Dialog removed */}
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Strategic Initiatives - Detailed View</DialogTitle>
          </DialogHeader>
          
          {baseData.strategicInitiatives && baseData.strategicInitiatives.length > 0 ? (
            <div className="space-y-4">
              {baseData.strategicInitiatives.map((initiative, idx) => {
                const hasMapping = initiative.parameter_mapping && 
                                  Object.values(initiative.parameter_mapping).some(v => v > 0);
                
                return (
                  <Card key={idx} className="border-l-4 border-l-purple-500">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{initiative.name}</CardTitle>
                          <CardDescription className="mt-2">{initiative.description}</CardDescription>
                        </div>
                        {hasMapping && (
                          <Badge className="bg-purple-100 text-purple-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Mapped
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm text-slate-700">Initiative Details</h4>
                          {initiative.budget > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Budget:</span>
                              <span className="font-semibold">{formatCurrency(initiative.budget)}</span>
                            </div>
                          )}
                          {initiative.timeline && (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Timeline:</span>
                              <span className="font-semibold">{initiative.timeline}</span>
                            </div>
                          )}
                          {initiative.expected_impact && Object.keys(initiative.expected_impact).length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-slate-600 font-semibold mb-1">Expected Impacts:</p>
                              <div className="space-y-1 text-xs">
                                {Object.entries(initiative.expected_impact).map(([key, value]) => (
                                  <div key={key} className="flex justify-between">
                                    <span className="text-slate-600">{key.replace(/_/g, ' ')}:</span>
                                    <span className="font-medium">{value}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm text-slate-700">Scenario Parameter Mapping</h4>
                          {hasMapping ? (
                            <div className="space-y-2">
                              {initiative.parameter_mapping.revenueGrowth > 0 && (
                                <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                                  <span className="text-sm">Revenue Growth</span>
                                  <Badge className="bg-green-600">+{initiative.parameter_mapping.revenueGrowth}%</Badge>
                                </div>
                              )}
                              {initiative.parameter_mapping.costReduction > 0 && (
                                <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                                  <span className="text-sm">Cost Reduction</span>
                                  <Badge className="bg-blue-600">+{initiative.parameter_mapping.costReduction}%</Badge>
                                </div>
                              )}
                              {initiative.parameter_mapping.pricingChange > 0 && (
                                <div className="flex items-center justify-between p-2 bg-purple-50 rounded">
                                  <span className="text-sm">Pricing Change</span>
                                  <Badge className="bg-purple-600">+{initiative.parameter_mapping.pricingChange}%</Badge>
                                </div>
                              )}
                              {initiative.parameter_mapping.marketExpansion > 0 && (
                                <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
                                  <span className="text-sm">Market Expansion</span>
                                  <Badge className="bg-orange-600">+{initiative.parameter_mapping.marketExpansion}%</Badge>
                                </div>
                              )}
                              {initiative.parameter_mapping.operationalEfficiency > 0 && (
                                <div className="flex items-center justify-between p-2 bg-teal-50 rounded">
                                  <span className="text-sm">Operational Efficiency</span>
                                  <Badge className="bg-teal-600">+{initiative.parameter_mapping.operationalEfficiency}%</Badge>
                                </div>
                              )}
                              
                              {/* Removed initiative parameter application feature */}
                            </div>
                          ) : (
                            <div className="p-4 bg-slate-50 rounded text-sm text-slate-500 italic">
                              No parameter mappings have been defined for this initiative. Mappings are auto-generated based on initiative keywords.
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-400" />
              <p>No strategic initiatives found.</p>
              <p className="text-sm mt-2">Create strategic initiatives in the Strategic Framework section to see them here.</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInitiativesDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Financial Constraints Dialog removed */}
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Financial Management Constraints</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Constraint Definitions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                  Active Constraints
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold">Revenue Growth</span>
                      <Badge variant="outline">Max 50%</Badge>
                    </div>
                    <p className="text-xs text-slate-600">
                      High growth may strain working capital and cash flow
                    </p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold">Cost Reduction</span>
                      <Badge variant="outline">Max 30%</Badge>
                    </div>
                    <p className="text-xs text-slate-600">
                      Aggressive cuts may impact operational capacity
                    </p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold">Market Expansion</span>
                      <Badge variant="outline">Max 25%</Badge>
                    </div>
                    <p className="text-xs text-slate-600">
                      Expansion requires additional capital investment
                    </p>
                  </div>
                  
                  <div className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold">Combined Growth</span>
                      <Badge variant="outline">Max 100%</Badge>
                    </div>
                    <p className="text-xs text-slate-600">
                      Revenue + Market expansion must stay within limits
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Current Scenario Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Activity className="h-5 w-5 mr-2 text-blue-600" />
                  Current Scenario Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {scenarioParams[activeScenario] && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                        <span>Revenue Growth:</span>
                        <span className="font-semibold">
                          {scenarioParams[activeScenario].revenueGrowth || 0}%
                          {(scenarioParams[activeScenario].revenueGrowth || 0) > 50 && (
                            <AlertTriangle className="h-4 w-4 text-yellow-600 inline ml-2" />
                          )}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                        <span>Cost Reduction:</span>
                        <span className="font-semibold">
                          {scenarioParams[activeScenario].costReduction || 0}%
                          {(scenarioParams[activeScenario].costReduction || 0) > 30 && (
                            <AlertTriangle className="h-4 w-4 text-yellow-600 inline ml-2" />
                          )}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                        <span>Market Expansion:</span>
                        <span className="font-semibold">
                          {scenarioParams[activeScenario].marketExpansion || 0}%
                          {(scenarioParams[activeScenario].marketExpansion || 0) > 25 && (
                            <AlertTriangle className="h-4 w-4 text-yellow-600 inline ml-2" />
                          )}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                        <span>Combined Growth:</span>
                        <span className="font-semibold">
                          {((scenarioParams[activeScenario].revenueGrowth || 0) + 
                            (scenarioParams[activeScenario].marketExpansion || 0))}%
                          {(((scenarioParams[activeScenario].revenueGrowth || 0) + 
                             (scenarioParams[activeScenario].marketExpansion || 0)) > 100) && (
                            <AlertCircle className="h-4 w-4 text-red-600 inline ml-2" />
                          )}
                        </span>
                      </div>
                    </div>
                    
                    {(constraintWarnings.length === 0 && constraintErrors.length === 0) && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                        <span className="text-sm text-green-900 font-medium">
                          All constraints satisfied
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Active Warnings & Errors */}
            {(constraintWarnings.length > 0 || constraintErrors.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center text-orange-900">
                    <AlertTriangle className="h-5 w-5 mr-2" />
                    Active Issues ({constraintWarnings.length + constraintErrors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {constraintErrors.map((error, idx) => (
                    <div key={`error-${idx}`} className="flex items-start p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-900">Constraint Violation</p>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                      </div>
                    </div>
                  ))}
                  
                  {constraintWarnings.map((warning, idx) => (
                    <div key={`warning-${idx}`} className="flex items-start p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-yellow-900">Warning</p>
                        <p className="text-sm text-yellow-700 mt-1">{warning}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start">
                <HelpCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">About Financial Constraints</p>
                  <p>
                    These constraints ensure your scenarios remain financially viable. Warnings indicate approaches to caution zones, 
                    while errors prevent scenarios that would violate fundamental financial health limits. Constraints are based on 
                    industry best practices and your company's financial structure.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConstraintsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      

      {/* Upgrade Dialog */}
      {upgradeData && (
        <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <UpgradePrompt
              limitType={upgradeData.limitType}
              currentUsage={upgradeData.currentUsage}
              totalLimit={upgradeData.totalLimit}
              upgradeOptions={upgradeData.upgradeOptions}
              message={upgradeData.message}
              limitExceeded={true}
            />
          </DialogContent>
        </Dialog>
      )}
      </div>
    </FeatureGate>
  );
};

export default ScenarioModeling;